/**
 * Firebase client SDK initialization.
 *
 * All config values are read from PUBLIC_ environment variables
 * so they are safe to expose in the client bundle (they are
 * restricted by Firestore/Storage Security Rules, not kept secret).
 *
 * In development, all services automatically connect to local emulators
 * when PUBLIC_USE_FIREBASE_EMULATORS=true.
 */

import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import { getFirestore, connectFirestoreEmulator, type Firestore } from 'firebase/firestore';
import { getAuth, initializeAuth, browserLocalPersistence, connectAuthEmulator, type Auth } from 'firebase/auth';
import { getStorage, connectStorageEmulator, type FirebaseStorage } from 'firebase/storage';
import {
	PUBLIC_FIREBASE_API_KEY,
	PUBLIC_FIREBASE_AUTH_DOMAIN,
	PUBLIC_FIREBASE_PROJECT_ID,
	PUBLIC_FIREBASE_STORAGE_BUCKET,
	PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
	PUBLIC_FIREBASE_APP_ID,
	PUBLIC_USE_FIREBASE_EMULATORS
} from '$env/static/public';

// ── Singleton instances ──────────────────────────────────────────────────────

let app: FirebaseApp;
let _db: Firestore;
let _auth: Auth;
let _storage: FirebaseStorage;
let _emulatorsConnected = false;

function getApp(): FirebaseApp {
	if (!app) {
		app =
			getApps()[0] ??
			initializeApp({
				apiKey: PUBLIC_FIREBASE_API_KEY,
				authDomain: PUBLIC_FIREBASE_AUTH_DOMAIN,
				projectId: PUBLIC_FIREBASE_PROJECT_ID,
				storageBucket: PUBLIC_FIREBASE_STORAGE_BUCKET,
				messagingSenderId: PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
				appId: PUBLIC_FIREBASE_APP_ID
			});
	}
	return app;
}

export function db(): Firestore {
	if (!_db) {
		_db = getFirestore(getApp());
		maybeConnectEmulators();
	}
	return _db;
}

export function auth(): Auth {
	if (!_auth) {
		if (typeof window !== 'undefined') {
			_auth = initializeAuth(getApp(), {
				persistence: browserLocalPersistence
			});
		} else {
			_auth = getAuth(getApp());
		}
		maybeConnectEmulators();
	}
	return _auth;
}

export function storage(): FirebaseStorage {
	if (!_storage) {
		_storage = getStorage(getApp());
		maybeConnectEmulators();
	}
	return _storage;
}

// ── Emulator connection ──────────────────────────────────────────────────────

function maybeConnectEmulators() {
	if (PUBLIC_USE_FIREBASE_EMULATORS !== 'true') return;

	if (!_emulatorsConnected && _auth) {
		connectAuthEmulator(_auth, 'http://localhost:9099', { disableWarnings: true });
	}
	if (!_emulatorsConnected && _db) {
		connectFirestoreEmulator(_db, 'localhost', 8080);
	}
	if (!_emulatorsConnected && _storage) {
		connectStorageEmulator(_storage, 'localhost', 9199);
	}

	if (_auth && _db && _storage) {
		_emulatorsConnected = true;
		console.info('[Firebase] Connected to local emulators');
	}
}
